import React, { createContext, useState, useContext, useEffect } from 'react';
import apiService from '../services/api';
import { calculateDeliveryPrice } from '../utils/deliveryUtils';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [notification, setNotification] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('cornBlissCart');
      if (savedCart) {
        setCartItems(JSON.parse(savedCart));
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    }
    setIsLoading(false);
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (!isLoading) {
      try {
        localStorage.setItem('cornBlissCart', JSON.stringify(cartItems));
      } catch (error) {
        console.error('Error saving cart:', error);
      }
    }
  }, [cartItems, isLoading]);

  const addToCart = (product) => {
    const existingItem = cartItems.find(item => item.id === product.id);
    
    if (existingItem) {
      setCartItems(
        cartItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCartItems([...cartItems, { ...product, quantity: 1 }]);
    }
    
    // Trigger notification
    setNotification({
      product: product.name || product.title || 'Item',
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(cartItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCartItems(
        cartItems.map(item =>
          item.id === productId ? { ...item, quantity } : item
        )
      );
    }
  };

  const incrementQuantity = (productId) => {
    setCartItems(
      cartItems.map(item =>
        item.id === productId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    );
  };

  const decrementQuantity = (productId) => {
    const item = cartItems.find(i => i.id === productId);
    if (item && item.quantity > 1) {
      setCartItems(
        cartItems.map(i =>
          i.id === productId
            ? { ...i, quantity: i.quantity - 1 }
            : i
        )
      );
    } else if (item) {
      removeFromCart(productId);
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getDeliveryPrice = () => {
    return calculateDeliveryPrice(cartItems);
  };

  const getTotalWithDelivery = () => {
    return getTotalPrice() + getDeliveryPrice();
  };

  // Booking-related functions
  const submitBooking = async (customerData) => {
    try {
      setApiError(null);
      const bookingPayload = {
        customerName: customerData.name,
        customerEmail: customerData.email,
        customerPhone1: customerData.phone1,
        customerPhone2: customerData.phone2,
        bookingDate: new Date(),
        items: cartItems.map(item => ({
          productId: item.id,
          productName: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        totalPrice: getTotalPrice(),
        status: 'Pending',
      };

      const response = await apiService.createBooking(bookingPayload);
      setCurrentBooking(response);
      setBookings([...bookings, response]);
      clearCart();
      return response;
    } catch (error) {
      setApiError(error.message);
      console.error('Error submitting booking:', error);
      throw error;
    }
  };

  const validateBooking = async (customerData) => {
    try {
      setApiError(null);
      const bookingPayload = {
        customerName: customerData.name,
        customerEmail: customerData.email,
        customerPhone1: customerData.phone1,
        customerPhone2: customerData.phone2,
        items: cartItems.map(item => ({
          productId: item.id,
          productName: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        totalPrice: getTotalPrice(),
      };

      const result = await apiService.validateBooking(bookingPayload);
      return result;
    } catch (error) {
      setApiError(error.message);
      console.error('Error validating booking:', error);
      throw error;
    }
  };

  const fetchBookings = async () => {
    try {
      setApiError(null);
      const data = await apiService.getBookings();
      setBookings(data);
    } catch (error) {
      setApiError(error.message);
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchBooking = async (id) => {
    try {
      setApiError(null);
      const data = await apiService.getBooking(id);
      setCurrentBooking(data);
      return data;
    } catch (error) {
      setApiError(error.message);
      console.error('Error fetching booking:', error);
      throw error;
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        incrementQuantity,
        decrementQuantity,
        clearCart,
        getTotalPrice,
        getTotalItems,
        getDeliveryPrice,
        getTotalWithDelivery,
        isLoading,
        bookings,
        currentBooking,
        apiError,
        submitBooking,
        validateBooking,
        fetchBookings,
        fetchBooking,
        notification,
        clearNotification: () => setNotification(null),
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
